import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Tabs,
  Tab,
  Chip,
  CircularProgress,
  Alert,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Link,
} from '@mui/material';
import {
  ArrowBack,
  AutoFixHigh,
  Refresh,
  PlayArrow,
} from '@mui/icons-material';
import {
  materialTakeoffService,
  jobStatusColor,
  jobStatusLabel,
  takeoffStatusColor,
  takeoffStatusLabel,
} from '../services/materialTakeoffService';
import MaterialTakeoffReportSections from '../components/MaterialTakeoffReportSections';
import { realtimeService } from '../services/realtimeService';

const SECTION_TABS = [
  { key: 'overview', label: 'Overview' },
  { key: 'raw_cut_list', label: 'Raw cut list' },
  { key: 'nesting', label: 'Nesting' },
  { key: 'buy_list', label: 'Buy list' },
  { key: 'sheets', label: 'Sheet metal' },
  { key: 'weight', label: 'Weight' },
  { key: 'flags', label: 'Red flags' },
  { key: 'purchased', label: 'Purchased audit' },
];

export default function MaterialTakeoffDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [takeoff, setTakeoff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [savingBuyList, setSavingBuyList] = useState(false);
  const [sectionTab, setSectionTab] = useState(0);
  const [selectedBuyIds, setSelectedBuyIds] = useState([]);
  const [buyDraft, setBuyDraft] = useState([]);
  const pollRef = useRef(null);

  const load = useCallback(async ({ quiet = false } = {}) => {
    try {
      if (!quiet) setLoading(true);
      setError('');
      const res = await materialTakeoffService.get(id);
      const data = res.data;
      setTakeoff(data);
      setBuyDraft(data.buy_items || []);
      setSelectedBuyIds((data.buy_items || []).filter((r) => r.selected !== false && !r.applied_quote_item_id).map((r) => r.id));
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load material takeoff');
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const unsub = realtimeService.subscribe((event) => {
      if (event?.entity === 'material_takeoff' && String(event.entityId) === String(id)) {
        load({ quiet: true });
      }
      if (event?.entity === 'quote' && takeoff?.quote_id && String(event.entityId) === String(takeoff.quote_id)) {
        if (event?.action?.includes('material_takeoff')) {
          load({ quiet: true });
        }
      }
    });
    return unsub;
  }, [id, load, takeoff?.quote_id]);

  const jobRunning = useMemo(() => {
    const status = takeoff?.job?.status;
    return status === 'queued' || status === 'processing';
  }, [takeoff?.job?.status]);

  useEffect(() => {
    if (!jobRunning) {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = window.setInterval(() => load({ quiet: true }), 4000);
    return () => {
      if (pollRef.current) {
        window.clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [jobRunning, load]);

  const handleAnalyze = async () => {
    try {
      setAnalyzing(true);
      setError('');
      await materialTakeoffService.analyze(id);
      await load({ quiet: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to start analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const handleSaveBuyList = async () => {
    try {
      setSavingBuyList(true);
      await materialTakeoffService.saveBuyItems(id, buyDraft.map((row, index) => ({
        ...row,
        sort_order: index,
        selected: selectedBuyIds.includes(row.id),
      })));
      await load({ quiet: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save buy list');
    } finally {
      setSavingBuyList(false);
    }
  };

  const handleApplyToQuote = async () => {
    try {
      setApplying(true);
      await materialTakeoffService.applyToQuote(id, selectedBuyIds);
      await load({ quiet: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to apply buy list to quote');
    } finally {
      setApplying(false);
    }
  };

  const resultJson = takeoff?.result_json || null;

  const sectionContent = () => {
    if (sectionTab === 0) {
      return (
        <MaterialTakeoffReportSections resultJson={resultJson} buyItems={buyDraft} />
      );
    }
    if (!resultJson) {
      return (
        <Typography variant="body2" color="text.secondary">
          Run analysis to populate this section.
        </Typography>
      );
    }
    return <MaterialTakeoffReportSections resultJson={{
      ...resultJson,
      raw_cut_list: sectionTab === 1 ? resultJson.raw_cut_list : [],
      nesting_review: sectionTab === 2 ? resultJson.nesting_review : [],
      buy_list: sectionTab === 3 ? resultJson.buy_list : [],
      sheet_metal_summary: sectionTab === 4 ? resultJson.sheet_metal_summary : [],
      weight_estimate: sectionTab === 5 ? resultJson.weight_estimate : null,
      red_flags: sectionTab === 6 ? resultJson.red_flags : [],
      purchased_item_audit: sectionTab === 7 ? resultJson.purchased_item_audit : [],
      summary: sectionTab === 0 ? resultJson.summary : null,
    }} buyItems={sectionTab === 3 ? buyDraft : []} />;
  };

  if (loading && !takeoff) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!takeoff) {
    return <Alert severity="error">{error || 'Material takeoff not found'}</Alert>;
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, flexWrap: 'wrap' }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/material-takeoffs')}>
          Back
        </Button>
        <Typography variant="h5" sx={{ flex: 1 }}>{takeoff.title}</Typography>
        <Chip size="small" label={takeoffStatusLabel(takeoff.status)} color={takeoffStatusColor(takeoff.status)} variant="outlined" />
        {takeoff.job?.status && (
          <Chip size="small" label={jobStatusLabel(takeoff.job.status)} color={jobStatusColor(takeoff.job.status)} variant="outlined" />
        )}
        <Button size="small" startIcon={<Refresh />} onClick={() => load()}>Refresh</Button>
        <Button
          variant="contained"
          size="small"
          startIcon={analyzing || jobRunning ? <CircularProgress size={14} color="inherit" /> : <AutoFixHigh />}
          disabled={analyzing || jobRunning}
          onClick={handleAnalyze}
        >
          {jobRunning ? 'Analyzing…' : 'Analyze drawings'}
        </Button>
      </Box>

      <Paper sx={{ p: 1.5, mb: 2 }}>
        <Typography variant="body2">
          Quote:{' '}
          {takeoff.quote_id ? (
            <Link component={RouterLink} to={`/quotes/${takeoff.quote_id}`}>
              {takeoff.quote_number || `#${takeoff.quote_id}`}
            </Link>
          ) : '—'}
          {' · '}
          Customer: {takeoff.customer_name || '—'}
        </Typography>
        {takeoff.summary && (
          <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>{takeoff.summary}</Typography>
        )}
        {(takeoff.attachments || []).length > 0 && (
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Drawings: {(takeoff.attachments || []).map((a) => a.file_name).join(', ')}
          </Typography>
        )}
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {takeoff.job?.status === 'failed' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Analysis failed{takeoff.job.error_message ? `: ${takeoff.job.error_message}` : '.'}
        </Alert>
      )}

      <Paper sx={{ mb: 2 }}>
        <Tabs value={sectionTab} onChange={(_, v) => setSectionTab(v)} variant="scrollable" scrollButtons="auto">
          {SECTION_TABS.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
        <Box sx={{ p: 2 }}>{sectionContent()}</Box>
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1">Buy list (editable)</Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button size="small" disabled={savingBuyList} onClick={handleSaveBuyList}>
              {savingBuyList ? 'Saving…' : 'Save buy list'}
            </Button>
            {takeoff.quote_id && (
              <Button
                size="small"
                variant="contained"
                startIcon={<PlayArrow />}
                disabled={applying || selectedBuyIds.length === 0}
                onClick={handleApplyToQuote}
              >
                {applying ? 'Applying…' : `Apply to quote (${selectedBuyIds.length})`}
              </Button>
            )}
          </Box>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Line</TableCell>
                <TableCell>Qty</TableCell>
                <TableCell>Unit</TableCell>
                <TableCell>Applied</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {buyDraft.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5}>
                    <Typography variant="body2" color="text.secondary">No buy list items yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                buyDraft.map((row, index) => (
                  <TableRow key={row.id || index}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedBuyIds.includes(row.id)}
                        disabled={!!row.applied_quote_item_id}
                        onChange={(e) => {
                          setSelectedBuyIds((prev) => (
                            e.target.checked
                              ? [...prev, row.id]
                              : prev.filter((idVal) => idVal !== row.id)
                          ));
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={row.line_text || ''}
                        onChange={(e) => {
                          const next = [...buyDraft];
                          next[index] = { ...row, line_text: e.target.value };
                          setBuyDraft(next);
                        }}
                      />
                    </TableCell>
                    <TableCell>{row.quantity ?? '—'}</TableCell>
                    <TableCell>{row.unit ?? '—'}</TableCell>
                    <TableCell>{row.applied_quote_item_id ? 'Yes' : '—'}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
